import React, { useState, useEffect, useRef } from 'react';
import { ArrowDownToLine, ArrowUpFromLine, Building, Send, Clock, CheckCircle, XCircle, ChevronRight, RefreshCw, Eraser, FileText } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { SignatureCanvas } from 'react-signature-canvas';

const BACKOFFICE_API = '/api/backoffice';

// _FIX_AUTH_V1 — backoffice endpoints now accept the merchant JWT via dual auth
const getAuthHeaders = (extra?: Record<string, string>) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(extra || {}) };
};

// RDC banks. Equity Banque Commerciale du Congo is fee-free; others charge fees.
const DRC_BANKS = [
  'Equity BCDC',
  'Rawbank',
  'TMB (Trust Merchant Bank)',
  'Ecobank RDC',
  'BGFIBank RDC',
  'Access Bank RDC',
  'FBNBank RDC',
  'UBA RDC',
  'Sofibanque',
  'Afriland First Bank',
  'Standard Bank RDC',
  'Citibank RDC',
  'Bank of Africa (BOA)',
  'ProCredit Bank',
  'Solidaire Banque Internationale',
  'Banque Internationale pour l’Afrique au Congo (BIAC)',
  'FINCA RDC',
  'Advans Banque Congo',
  'Autre',
];
const FEE_FREE_BANK = 'Equity BCDC';

interface WithdrawalPhone {
  id: number;
  operator: string;
  phone_number: string;
  account_name: string;
  is_default: boolean;
}

interface WRequest {
  id: number;
  reference: string;
  status: string;
  amount: number;
  currency: string;
  destination_type: string;
  destination_account: string;
  method: string;
  created_at: string;
  acknowledged_at: string | null;
  initialized_at: string | null;
  approved_at: string | null;
  validated_at: string | null;
  paid_at: string | null;
  completed_at: string | null;
  rejection_reason: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-gray-100 text-gray-700',
  ACKNOWLEDGED: 'bg-blue-100 text-blue-700',
  INITIALIZED: 'bg-indigo-100 text-indigo-700',
  APPROVED: 'bg-emerald-100 text-emerald-700',
  VALIDATED: 'bg-teal-100 text-teal-700',
  PAID: 'bg-cyan-100 text-cyan-700',
  COMPLETED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-red-50 text-red-500',
};

const STATUS_STEPS = ['PENDING', 'ACKNOWLEDGED', 'INITIALIZED', 'APPROVED', 'VALIDATED', 'PAID', 'COMPLETED'];

function fmtAmt(n: number): string {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function numberToWords(num: number, currency: string): string {
  if (num === 0) return 'Zero';
  const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function convert(n: number): string {
    if (n < 10) return units[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + units[n % 10] : '');
    if (n < 1000) return units[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convert(n % 100) : '');
    if (n < 1000000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 1000000000) return convert(Math.floor(n / 1000000)) + ' Million' + (n % 1000000 ? ' ' + convert(n % 1000000) : '');
    return convert(Math.floor(n / 1000000000)) + ' Billion' + (n % 1000000000 ? ' ' + convert(n % 1000000000) : '');
  }

  return convert(Math.floor(num)) + ' ' + currency;
}

interface Props {
  direction: 'withdraw' | 'deposit';
}

const FundTransferRequest: React.FC<Props> = ({ direction }) => {
  const { user, merchantAccount } = useAuth();
  const merchantCode = merchantAccount?.merchant_code || (user as Record<string, unknown>)?.merchant_code as string || '';
  const institutionName = (user as Record<string, unknown>)?.institution_name as string || user?.firstname || '';
  const merchantName = institutionName || `${user?.firstname || ''} ${user?.lastname || ''}`.trim();

  const [tab, setTab] = useState<'form' | 'history'>('form');
  const [step, setStep] = useState<'details' | 'sign'>('details');

  // Form state
  const destinationType = 'bank' as const;
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('CDF');
  const [destinationAccount, setDestinationAccount] = useState('');
  const [destinationName, setDestinationName] = useState('');
  const [bankName, setBankName] = useState('');
  const [comment, setComment] = useState('');
  const [requesterName, setRequesterName] = useState(merchantName);
  const [place, setPlace] = useState('Kinshasa');
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ ok: boolean; msg: string } | null>(null);

  // Signature
  const sigRef = useRef<SignatureCanvas>(null);
  const [hasSigned, setHasSigned] = useState(false);

  const operator = '';

  // History
  const [requests, setRequests] = useState<WRequest[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (tab === 'history' && merchantCode) {
      setHistoryLoading(true);
      fetch(`${BACKOFFICE_API}/withdrawal-workflow/requests?merchant_type=api&status=all`, {
        headers: getAuthHeaders(),
      })
        .then(r => r.json())
        .then(data => setRequests((data || []).filter((r: WRequest) => r.merchant_code === merchantCode)))
        .catch(() => setRequests([]))
        .finally(() => setHistoryLoading(false));
    }
  }, [tab, merchantCode]);

  const clearSignature = () => {
    sigRef.current?.clear();
    setHasSigned(false);
  };

  const generatePDF = async (): Promise<string> => {
    const jspdfModule = await import('jspdf');
    const JsPDF = jspdfModule.jsPDF || jspdfModule.default;
    const doc = new JsPDF();
    const w = doc.internal.pageSize.getWidth();
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    const amt = parseFloat(amount) || 0;

    // Header
    doc.setFillColor(30, 30, 30);
    doc.rect(20, 15, w - 40, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Transfer Request Form', w / 2, 23, { align: 'center' });

    // FreshPay branding
    doc.setTextColor(200, 80, 20);
    doc.setFontSize(10);
    doc.text('FreshPay', w - 25, 12, { align: 'right' });

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    let y = 40;

    // Merchant Name
    doc.setFont('helvetica', 'bold');
    doc.text('MERCHANT NAME:', 25, y);
    doc.setFont('helvetica', 'normal');
    doc.text(merchantName, 70, y);
    y += 10;

    // Transfer instruction
    doc.setFont('helvetica', 'bold');
    doc.text('TRANSFER INSTRUCTION', 25, y);
    y += 8;

    // Table header
    doc.setFillColor(240, 240, 240);
    doc.rect(25, y - 4, w - 50, 8, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('From', 27, y + 1);
    doc.text('To: Bank/Wallet', 60, y + 1);
    doc.text('Account Number', 100, y + 1);
    doc.text('Amount', 150, y + 1);
    y += 12;

    // Table row
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const fromText = direction === 'withdraw' ? 'Wallet' : (destinationType === 'bank' ? bankName || 'Bank' : operator || 'Mobile Money');
    const toText = direction === 'withdraw' ? (destinationType === 'bank' ? bankName || 'Bank Account' : `${operator} Mobile Money`) : 'Wallet';
    doc.text(fromText, 27, y);
    doc.text(toText, 60, y);
    doc.text(destinationAccount, 100, y);
    doc.text(`${fmtAmt(amt)} ${currency}`, 150, y);
    y += 15;

    // Amount in words
    doc.setFont('helvetica', 'bold');
    doc.text('Amount in words:', 25, y);
    doc.setFont('helvetica', 'normal');
    doc.text(numberToWords(amt, currency), 65, y);
    y += 15;

    // Direction
    doc.setFont('helvetica', 'bold');
    doc.text('Transfer Type:', 25, y);
    doc.setFont('helvetica', 'normal');
    doc.text(direction === 'withdraw' ? 'Wallet to Bank/Mobile Money' : 'Bank/Mobile Money to Wallet', 65, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.text('Destination Type:', 25, y);
    doc.setFont('helvetica', 'normal');
    doc.text(destinationType === 'bank' ? 'Bank Account' : 'Mobile Money', 65, y);
    y += 15;

    // Footer section
    doc.line(25, y, w - 25, y);
    y += 10;

    // Place and requester
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Place:', 25, y);
    doc.setFont('helvetica', 'normal');
    doc.text(place, 45, y);

    doc.setFont('helvetica', 'bold');
    doc.text('Name of requester:', w / 2, y);
    doc.setFont('helvetica', 'normal');
    doc.text(requesterName, w / 2 + 40, y);
    y += 10;

    doc.setFont('helvetica', 'bold');
    doc.text('Date:', 25, y);
    doc.setFont('helvetica', 'normal');
    doc.text(dateStr, 45, y);

    // Signature
    doc.setFont('helvetica', 'bold');
    doc.text('Signature:', w / 2, y);
    y += 5;

    if (sigRef.current && !sigRef.current.isEmpty()) {
      const sigData = sigRef.current.toDataURL('image/png');
      doc.addImage(sigData, 'PNG', w / 2, y, 50, 20);
    }

    y += 25;

    // Reference and timestamp
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated electronically on ${now.toISOString()} | Merchant: ${merchantCode}`, 25, y);

    return doc.output('datauristring');
  };

  const handleSubmit = async () => {
    if (!hasSigned) return;
    setSubmitting(true);
    setSubmitResult(null);

    try {
      const pdfData = await generatePDF();

      const res = await fetch(`${BACKOFFICE_API}/withdrawal-workflow/submit`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          merchant_type: 'api',
          merchant_code: merchantCode,
          merchant_name: merchantName,
          amount: parseFloat(amount),
          currency,
          destination_type: destinationType,
          destination_account: destinationAccount,
          destination_name: destinationName || bankName,
          method: operator,
          signed_form_url: pdfData,
          comment: `${direction === 'withdraw' ? 'Wallet to Bank/Mobile' : 'Bank/Mobile to Wallet'}${comment ? ': ' + comment : ''}`,
          submitted_by: requesterName,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        const errMsg = errData?.detail
          ? (Array.isArray(errData.detail) ? errData.detail.map((d: { msg?: string }) => d.msg).join(', ') : errData.detail)
          : `Server error (${res.status})`;
        setSubmitResult({ ok: false, msg: errMsg });
        return;
      }
      const data = await res.json();
      if (data.success) {
        setSubmitResult({ ok: true, msg: `Request submitted successfully. Reference: ${data.reference}` });
        setAmount(''); setDestinationAccount(''); setDestinationName(''); setBankName(''); setComment('');
        setStep('details'); clearSignature();
      } else {
        setSubmitResult({ ok: false, msg: data.detail || data.error || 'Failed to submit' });
      }
    } catch (err) {
      console.error('Submit error:', err);
      setSubmitResult({ ok: false, msg: `Error: ${err instanceof Error ? err.message : 'Unknown error'}` });
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedToSign = !!amount && !!destinationAccount;
  const isWithdraw = direction === 'withdraw';

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        <button onClick={() => setTab('form')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'form' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}>
          {isWithdraw ? <><ArrowDownToLine className="inline w-4 h-4 mr-1" /> New Request</> : <><ArrowUpFromLine className="inline w-4 h-4 mr-1" /> New Request</>}
        </button>
        <button onClick={() => setTab('history')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'history' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}>
          <Clock className="inline w-4 h-4 mr-1" /> My Requests
        </button>
      </div>

      {/* Form */}
      {tab === 'form' && (
        <>
          {/* Step indicator */}
          <div className="flex items-center gap-3">
            <button onClick={() => setStep('details')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                step === 'details' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}>1. Transfer Details</button>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <button onClick={() => canProceedToSign && setStep('sign')}
              disabled={!canProceedToSign}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                step === 'sign' ? 'bg-purple-600 text-white' : canProceedToSign ? 'bg-gray-100 text-gray-600' : 'bg-gray-50 text-gray-300'
              }`}>2. Sign & Submit</button>
          </div>

          {submitResult && (
            <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${submitResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {submitResult.ok ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {submitResult.msg}
            </div>
          )}

          {/* Step 1: Details */}
          {step === 'details' && (
            <div className="space-y-5">
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <p className="text-sm text-purple-800">
                  {isWithdraw
                    ? 'Request to transfer funds from your wallet to a bank account. This request will be reviewed by the finance team.'
                    : 'Request to deposit funds into your wallet from a bank account.'}
                </p>
                {isWithdraw && (
                  <p className="text-xs text-purple-600 mt-1">
                    Pour les retraits Mobile Money, utilisez la section Retrait dans le menu principal.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Destination Type</label>
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border-2 border-purple-500 bg-purple-50 text-purple-700 text-sm font-medium">
                      <Building className="w-4 h-4" /> Bank Account
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                  <div className="flex gap-2">
                    {['CDF', 'USD'].map(c => (
                      <button key={c} type="button" onClick={() => setCurrency(c)}
                        className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors ${
                          currency === c ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 text-gray-600'
                        }`}>{c}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none" />
                  {amount && <p className="text-xs text-gray-400 mt-1">{numberToWords(parseFloat(amount) || 0, currency)}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account Number *</label>
                  <input type="text" value={destinationAccount} onChange={e => setDestinationAccount(e.target.value)}
                    placeholder="Enter bank account number"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm outline-none" />
                </div>

                {destinationType === 'bank' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
                    <select
                      value={bankName}
                      onChange={e => setBankName(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                    >
                      <option value="">Sélectionner une banque...</option>
                      {DRC_BANKS.map(b => (
                        <option key={b} value={b}>{b === FEE_FREE_BANK ? `${b} — sans frais` : b}</option>
                      ))}
                    </select>
                    {bankName === FEE_FREE_BANK && (
                      <div className="mt-2 flex items-start gap-2 text-xs text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-md px-2.5 py-2">
                        <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span><strong>Equity BCDC :</strong> aucun frais de transfert appliqué côté Moko.</span>
                      </div>
                    )}
                    {bankName && bankName !== FEE_FREE_BANK && (
                      <div className="mt-2 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-md px-2.5 py-2">
                        <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>Des frais de transfert seront appliqués pour <strong>{bankName}</strong>. Seul Equity BCDC est exempté de frais.</span>
                      </div>
                    )}
                  </div>
                )}

                {destinationType === 'bank' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name</label>
                    <input type="text" value={destinationName} onChange={e => setDestinationName(e.target.value)} placeholder="Name on the account"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm outline-none" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Comment (optional)</label>
                <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2} placeholder="Add notes..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm outline-none resize-none" />
              </div>

              <button type="button" onClick={() => setStep('sign')} disabled={!canProceedToSign}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors">
                Continue to Sign <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Step 2: Sign */}
          {step === 'sign' && (
            <div className="space-y-5">
              {/* Summary */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Transfer Summary</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Type:</span> <span className="font-medium">{isWithdraw ? 'Wallet to Bank/Mobile' : 'Bank/Mobile to Wallet'}</span></div>
                  <div><span className="text-gray-500">Amount:</span> <span className="font-bold text-lg">{fmtAmt(parseFloat(amount) || 0)} {currency}</span></div>
                  <div><span className="text-gray-500">Destination:</span> <span className="font-medium">{destinationType === 'bank' ? 'Bank Account' : `Mobile Money (${operator})`}</span></div>
                  <div><span className="text-gray-500">Account:</span> <span className="font-mono">{destinationAccount}</span></div>
                  {bankName && <div><span className="text-gray-500">Bank:</span> <span className="font-medium">{bankName}</span></div>}
                  {destinationName && <div><span className="text-gray-500">Holder:</span> <span className="font-medium">{destinationName}</span></div>}
                </div>
                <p className="text-xs text-gray-400 mt-2">{numberToWords(parseFloat(amount) || 0, currency)}</p>
              </div>

              {/* Requester info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name of Requester</label>
                  <input type="text" value={requesterName} onChange={e => setRequesterName(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Place</label>
                  <input type="text" value={place} onChange={e => setPlace(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm outline-none" />
                </div>
              </div>

              {/* Signature */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Signature *</label>
                  <button type="button" onClick={clearSignature}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-500">
                    <Eraser className="w-3 h-3" /> Clear
                  </button>
                </div>
                <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white" style={{ height: 150 }}>
                  <SignatureCanvas
                    ref={sigRef}
                    canvasProps={{ className: 'w-full h-full', style: { width: '100%', height: '150px' } }}
                    onEnd={() => setHasSigned(true)}
                    penColor="black"
                    backgroundColor="white"
                  />
                </div>
                {!hasSigned && <p className="text-xs text-amber-600 mt-1">Please draw your signature above to proceed</p>}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button type="button" onClick={() => setStep('details')}
                  className="flex-1 px-6 py-3 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50">
                  Back
                </button>
                <button type="button" onClick={handleSubmit} disabled={submitting || !hasSigned}
                  className="flex-[2] flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors">
                  {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {submitting ? 'Submitting...' : 'Sign & Submit Transfer Request'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* History */}
      {tab === 'history' && (
        <div>
          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No transfer requests yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map(r => {
                const stepIndex = STATUS_STEPS.indexOf(r.status);
                return (
                  <div key={r.id} className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs text-gray-500">{r.reference}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[r.status] || 'bg-gray-100'}`}>{r.status}</span>
                      </div>
                      <span className="text-lg font-bold text-gray-900">{fmtAmt(r.amount)} <span className="text-sm text-gray-500">{r.currency}</span></span>
                    </div>
                    {r.status !== 'REJECTED' && r.status !== 'CANCELLED' && (
                      <div className="flex items-center gap-0.5 mb-2">
                        {STATUS_STEPS.map((s, i) => (
                          <div key={s} className={`flex-1 h-1.5 rounded-full ${i <= stepIndex ? 'bg-purple-500' : 'bg-gray-200'}`} />
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{r.destination_type === 'bank' ? 'Bank' : 'Mobile Money'} - {r.destination_account}</span>
                      <span>{r.created_at?.slice(0, 16)}</span>
                    </div>
                    {r.rejection_reason && (
                      <div className="mt-2 p-2 rounded bg-red-50 text-xs text-red-700">Rejected: {r.rejection_reason}</div>
                    )}
                    <button onClick={async () => {
                      try {
                        const res = await fetch(`${BACKOFFICE_API}/withdrawal-workflow/requests/${r.id}/pdf`, { headers: getAuthHeaders() });
                        const data = await res.json();
                        if (data.pdf) {
                          const w = window.open();
                          if (w) { w.document.write(`<iframe src="${data.pdf}" style="width:100%;height:100%;border:none;"></iframe>`); }
                        }
                      } catch { /* */ }
                    }}
                      className="mt-2 flex items-center gap-1 text-xs text-purple-600 hover:underline">
                      <FileText className="w-3 h-3" /> View Signed Form
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FundTransferRequest;
